import {EntitetType, ForelderModell, OppgaveModell, Oppgavestatus, TilgangsrolleMedlem, ZonedDateTimeSerialisert} from "../tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erZonedDateTimePassert, erZonedDateTimeTidligereEnn, lesZonedDateTimeSerialisertOpt, plainDateFraZonedDateTime, sammenliknZonedDateTime, skrivZonedDateTimeSerialisert, zonedDateTimeSluttPåDagenFraPlainDate} from "../støttefunksjoner/temporal";
import {Temporal} from "temporal-polyfill";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";
import {concat, interval, Observable, of, switchMap} from "rxjs";
import {map} from "rxjs/operators";
import {DestroyRef, Signal} from "@angular/core";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import ZonedDateTime = Temporal.ZonedDateTime;
import PlainDate = Temporal.PlainDate;
import {PotensiellForelderModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";

const debug = initDebugger('oppgaveModellHjelpere');

/** Sorterer oppgaver etter ihht {@link #sorterOppgave} */
export function sorterOppgaver(oppgave: OppgaveModell[]) {
    return oppgave.sort(sorterOppgave);
}

/** Sorterer oppgaver etter frist stigende, tittel stigende.
 * Returnerer negativt tall dersom a sorterer før b, positivt dersom a sorterer etter b, 0 dersom like.
 * Oppgaver uten frist sorterer etter oppgaver med frist.
 * Må holdes i synk med no.systemfabrikken.lederplan.domene.oppgave.Oppgave#compareTo */
export function sorterOppgave(a: OppgaveModell, b: OppgaveModell): number {
    if (a === b) return 0;

    let sortering = sammenliknZonedDateTime(lesZonedDateTimeSerialisertOpt(a.frist), lesZonedDateTimeSerialisertOpt(b.frist));
    if (sortering != 0) return sortering;

    sortering = a.tittel.localeCompare(b.tittel)
    if (sortering != 0) return sortering;

    return a.id.localeCompare(b.id);
}

/** Returnerer oppgave.frist som ZonedDateTime i UTC-tidssone.
 * I UTC da frist per konvensjon blir satt til 23:59:59 i UTC */
export function oppgaveFristUTC(oppgave: Pick<OppgaveModell, 'frist'>): ZonedDateTime | null {
    return lesZonedDateTimeSerialisertOpt(oppgave.frist, Tidssoneinformasjon.UTC);
}

/** Returnerer oppgave.frist som PlainDate i UTC-tidssone.
 * I UTC da frist per konvensjon blir satt til 23:59:59 i UTC */
export function oppgaveFristSomPlainDateUTC(oppgave: Pick<OppgaveModell, 'frist'>): PlainDate | null {
    const frist = oppgaveFristUTC(oppgave);
    if (erNil(frist)) return null;
    return plainDateFraZonedDateTime(frist, Tidssoneinformasjon.UTC);
}

/** Returnerer angitt frist som ZonedDateTime i UTC-tidssone med klokke satt til 23:59:59. */
export function oppgaveFristSomSerialisertUTC(frist: PlainDate | null): ZonedDateTimeSerialisert | null {
    return erIkkeNil(frist) ?
      skrivZonedDateTimeSerialisert(zonedDateTimeSluttPåDagenFraPlainDate(frist, Tidssoneinformasjon.UTC)) :
      null;
}

export function oppgaveUtførtdato(oppgave: OppgaveModell): ZonedDateTime | null {
    return lesZonedDateTimeSerialisertOpt(oppgave.utfoertDato);
}

export function oppgaveUtførtdatoSomPlainDate(oppgave: OppgaveModell, tidssone: Tidssoneinformasjon): PlainDate | null {
    const utført = oppgaveUtførtdato(oppgave);
    if (erNil(utført)) return null;
    return plainDateFraZonedDateTime(utført, tidssone);
}

export function erOppgaveForfalt(oppgave: OppgaveModell, klokke: Klokke): boolean {
    if (oppgave.utfoert) return false;
    const frist = oppgaveFristUTC(oppgave);
    if (erNil(frist)) return false;
    return erZonedDateTimeTidligereEnn(frist, klokke.nåSomZonedDateTime());
}

/** Returnerer informasjon om antall oppgaver og hvor mange av disse som har forfalt */
export function antallOppgaverForfalt(oppgaver: OppgaveModell[], klokke: Klokke): OppgaverInformasjon {
    return {
        antallTotalt: oppgaver.length,
        antallForfalt: oppgaver.filter(oppgave => erOppgaveForfalt(oppgave, klokke)).length
    };
}

export interface OppgaverInformasjon {
    antallTotalt: number;
    antallForfalt: number;
}

/** Sann dersom bruker kan redigere oppgave */
//@todo bør ikke den som er satt som ansvarlig kunne redigere oppgave?
export function kanOppgaveRedigeres(oppgave: OppgaveModell, kontekst: Brukerkontekst): boolean {
    return oppgave.harForelder || oppgave.ansvarlig == null ?
      kontekst.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, oppgave.eiendeGruppeId) :
      oppgave.ansvarlig.brukerId === kontekst.bruker.id;
}

/** Returnerer status for oppgave, status inkluderer i motsetning til oppgave.status
 * her statusen 'forfalt' */
export function oppgaveStatus(oppgave: OppgaveModell, klokke: Klokke): OppgavestatusInklOverskredet {
    if (oppgave.status == Oppgavestatus.utført) return OppgavestatusInklOverskredet.utfoert;

    if (erIkkeNil(oppgave.frist) && erZonedDateTimePassert(oppgaveFristUTC(oppgave)!, klokke))
        return OppgavestatusInklOverskredet.overskredet;

    if (oppgave.status == Oppgavestatus.påbegynt) return OppgavestatusInklOverskredet.paabegynt;
    if (erNil(oppgave.frist)) return OppgavestatusInklOverskredet.uten_frist;

    return OppgavestatusInklOverskredet.ikke_paabegynt;
}

/** Returnerer status beregnet med {@link #oppgaveStatus} hvert 60. sekund */
export function oppgaveStatusSomObserverbar(oppgave: OppgaveModell, klokke: Klokke): Observable<OppgavestatusInklOverskredet> {
    return concat(of(null), interval(1000 * 60)) //oppdater ved instansiering, og så hvert 60. sekund
      .pipe(map(() => oppgaveStatus(oppgave, klokke)));
}

/** Returnerer status beregnet med {@link #oppgaveStatus} hvert 60. sekund */
export function oppgaveStatusSomSignal(oppgave: Signal<OppgaveModell>, klokke: Klokke, destroyRef: DestroyRef): Signal<OppgavestatusInklOverskredet> {
    return toSignal(
      toObservable(oppgave)
        .pipe(
          switchMap(oppgave => oppgaveStatusSomObserverbar(oppgave, klokke)),
          takeUntilDestroyed(destroyRef)),
      {initialValue: OppgavestatusInklOverskredet.ikke_paabegynt});
}

export function erSaksoppgave(oppgave: OppgaveModell): oppgave is (OppgaveModell & { forelderRef: { type: EntitetType.sak } }) {
    return oppgave.forelderRef?.type === EntitetType.sak;
}

export function erBeslutningsoppgave(oppgave: OppgaveModell): oppgave is (OppgaveModell & { forelderRef: { type: EntitetType.beslutning } }) {
    return oppgave.forelderRef?.type === EntitetType.beslutning;
}

export function forelderModellFraOppgave(oppgave: OppgaveModell): PotensiellForelderModell {
    return {harForelder: oppgave.harForelder, beskrivelse: oppgave.forelderBeskrivelse, entitetRef: oppgave.forelderRef};
}

/** Utvider {@linkn Oppgavestatus} med status for 'overskredet' og 'uten frist' */
export enum OppgavestatusInklOverskredet {
    ikke_paabegynt,
    paabegynt,
    utfoert,
    overskredet,
    uten_frist
}
